import {
    NovedadesExportService,
    NovedadesExportServiceModel,
} from "@services/NovedadesExportService";
import { NovedadesExportAnalyticsHelper } from "@core/Analytics/NovedadesExportAnalyticsHelper";
import { HollowedButton, NormalButton } from "@shared/designSystem/Button";
import {
    HorizontalContainer,
    TableCell,
    TableHeaderCell,
    TableHeaderContainer,
    TableRowContainer,
    VerticalContainer
} from "@shared/designSystem/Containers";
import { Text } from "@shared/designSystem/Texts";
import designSystemStyles from "@shared/styles/designSystemStyles";
import { Description } from "@shared/utils/DescriptionsHelper";
import { useState } from "react";

interface ExportNovedadesSummaryStepScreenProps {
    process: NovedadesExportServiceModel.ProcessRecord;
    service: NovedadesExportService;
    onProcessRestarted?: (process: NovedadesExportServiceModel.ProcessRecord) => void;
}

interface SummaryRow {
    label: string;
    value: string;
}

interface SummaryTableProps {
    leftHeader: string;
    rightHeader: string;
    rows: SummaryRow[];
}

const tableWidth = "720px";
const leftColumnWidth = "55%";
const rightColumnWidth = "45%";

const SummaryTable: React.FC<SummaryTableProps> = ({
    leftHeader,
    rightHeader,
    rows,
}) => {
    return (
        <VerticalContainer width={tableWidth} maxWidth="100%" gap={designSystemStyles.gapSm} margin="0">
            <TableHeaderContainer height={designSystemStyles.sizeRowMedium}>
                <TableHeaderCell text={leftHeader} width={leftColumnWidth} />
                <TableHeaderCell text={rightHeader} width={rightColumnWidth} justifyContent="right" />
            </TableHeaderContainer>
            <div style={{ display: "flex", flexDirection: "column", gap: designSystemStyles.gapNone, width: "100%" }}>
                {rows.map((row) => (
                    <TableRowContainer key={row.label}>
                        <TableCell text={row.label} width={leftColumnWidth} truncate={true} />
                        <TableCell text={row.value} width={rightColumnWidth} justifyContent="right" truncate={true} />
                    </TableRowContainer>
                ))}
            </div>
        </VerticalContainer>
    );
};

interface RestartProcessingInletProps {
    isConfirming: boolean;
    onCancel: () => void;
    onConfirm: () => void;
}

const RestartProcessingInlet: React.FC<RestartProcessingInletProps> = ({
    isConfirming,
    onCancel,
    onConfirm,
}) => {
    return (
        <VerticalContainer
            gap={designSystemStyles.gapMd}
            margin="0"
            padding={designSystemStyles.paddingMd}
            backgroundColor={designSystemStyles.colorBackgroundGray3}
            borderRadius={designSystemStyles.cornerRadiusComponent}
        >
            <Text
                text={"¿Reiniciar el procesamiento?".localized()}
                markdown={true}
            />
            <Text
                text={"Se volverán a calcular los datos de la exportación y se reemplazará el resultado actual.".localized()}
                color={designSystemStyles.colorTextSecondary}
            />
            <HorizontalContainer justifyContent="flex-end" gap={designSystemStyles.gapSm}>
                <HollowedButton
                    text={"Cancelar".localized()}
                    onClick={onCancel}
                    disabled={isConfirming}
                    width="160px"
                />
                <NormalButton
                    text={"Reiniciar".localized()}
                    onClick={onConfirm}
                    loading={isConfirming}
                    disabled={isConfirming}
                    width="180px"
                />
            </HorizontalContainer>
        </VerticalContainer>
    );
};

const minutesToHours = (minutes: number | undefined): string => {
    return Description.Number.decimal((minutes ?? 0) / 60, {
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
    });
};

const minutesToHoursPerEmployee = (minutes: number | undefined, employeeCount: number): string => {
    const safeEmployeeCount = Math.max(employeeCount, 1);
    const value = (minutes ?? 0) / 60 / safeEmployeeCount;
    return Description.Number.decimal(value, {
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
    });
};

const formatNumber = (value: number | undefined): string => {
    return Description.Number.decimal(value);
};

const selectionModeText = (process: NovedadesExportServiceModel.ProcessRecord): string => {
    if (process.employeeSelectionMode === "ALL_WITH_DATA") {
        return "Todos los colaboradores".localized();
    }

    const selectedCount = process.selectedEmployeeIDs?.length ?? 0;
    if (process.employeeSelectionMode === "ONE") {
        return `${"Un colaborador".localized()} (${selectedCount})`;
    }

    return `${"Colaboradores específicos".localized()} (${selectedCount})`;
};

const ExportNovedadesSummaryStepScreen: React.FC<ExportNovedadesSummaryStepScreenProps> = ({
    process,
    service,
    onProcessRestarted,
}) => {
    const [isRestarting, setIsRestarting] = useState(false);
    const [isRestartConfirmationOpen, setIsRestartConfirmationOpen] = useState(false);
    const [restartError, setRestartError] = useState<string | null>(null);
    const totals = process.totals ?? {};
    const employeeCount = process.processedEmployeeCount || process.employeeCount || 0;
    const parameterRows: SummaryRow[] = [
        { label: "Desde".localized(), value: process.dateFrom || "N/D" },
        { label: "Hasta".localized(), value: process.dateTo || "N/D" },
        { label: "Colaboradores".localized(), value: selectionModeText(process) },
        { label: "Período".localized(), value: process.period || "N/D" },
    ];
    const rows: SummaryRow[] = [
        { label: "Horas acumuladas".localized(), value: minutesToHours(totals.totalMinutes) },
        { label: "Días ausentes".localized(), value: formatNumber(totals.absentDays) },
        { label: "Horas extra 100% por colaborador".localized(), value: minutesToHoursPerEmployee(totals.extra100Minutes, employeeCount) },
        { label: "Horas extra 50% por colaborador".localized(), value: minutesToHoursPerEmployee(totals.extra50Minutes, employeeCount) },
        { label: "Días feriados".localized(), value: formatNumber(totals.holidayDays) },
        { label: "Horas regulares por colaborador".localized(), value: minutesToHoursPerEmployee(totals.regularMinutes, employeeCount) },
        { label: "Días de vacaciones".localized(), value: formatNumber(totals.vacationDays) },
        { label: "Días trabajados".localized(), value: formatNumber(totals.workedDays) },
    ];

    const restartProcessing = async () => {
        setIsRestarting(true);
        setRestartError(null);
        NovedadesExportAnalyticsHelper.trackProcessingRestartSubmitted({
            processID: process.processID,
            status: process.status,
            currentStep: process.currentStep,
            origin: "summary",
        });

        try {
            const restartedProcess = await service.restartProcess(process.processID);
            NovedadesExportAnalyticsHelper.trackProcessingRestartSucceeded({
                processID: restartedProcess.processID,
                status: restartedProcess.status,
                currentStep: restartedProcess.currentStep,
                origin: "summary",
            });
            setIsRestartConfirmationOpen(false);
            onProcessRestarted?.(restartedProcess);
        } catch (error) {
            console.error(error);
            NovedadesExportAnalyticsHelper.trackProcessingRestartFailed({
                processID: process.processID,
                status: process.status,
                currentStep: process.currentStep,
                origin: "summary",
            });
            setRestartError("No se pudo reiniciar el procesamiento.".localized());
        } finally {
            setIsRestarting(false);
        }
    };

    return (
        <div style={{ display: "flex", flexDirection: "column", height: "100%", minHeight: 0, width: "100%", gap: designSystemStyles.gapLg }}>
            <div style={{ display: "flex", flexDirection: "column", gap: designSystemStyles.gapLg, overflowY: "auto", flex: 1, minHeight: 0, width: "100%", paddingTop: designSystemStyles.paddingMd }}>
                <SummaryTable
                    leftHeader={"Parámetro".localized()}
                    rightHeader={"Valor".localized()}
                    rows={parameterRows}
                />
                <SummaryTable
                    leftHeader={"Concepto".localized()}
                    rightHeader={"Total".localized()}
                    rows={rows}
                />
                <HorizontalContainer gap={designSystemStyles.gapLg}>
                    <Text text={`${"Colaboradores".localized()}: ${process.processedEmployeeCount}/${process.employeeCount}`} />
                    <Text text={`${"Errores".localized()}: ${process.errorsCount}`} />
                    <Text text={`${"Avisos".localized()}: ${process.warningsCount}`} />
                </HorizontalContainer>
            </div>
            <HorizontalContainer justifyContent="flex-end" gap={designSystemStyles.gapSm} width="100%">
                {restartError && (
                    <Text text={restartError} color={designSystemStyles.colorError} />
                )}
                <HollowedButton
                    text={"Reiniciar procesamiento".localized()}
                    onClick={() => setIsRestartConfirmationOpen(true)}
                    loading={isRestarting}
                    disabled={isRestarting || isRestartConfirmationOpen}
                    width="240px"
                />
            </HorizontalContainer>
            {isRestartConfirmationOpen && (
                <RestartProcessingInlet
                    isConfirming={isRestarting}
                    onCancel={() => setIsRestartConfirmationOpen(false)}
                    onConfirm={() => void restartProcessing()}
                />
            )}
        </div>
    );
};

export default ExportNovedadesSummaryStepScreen;
